import { ProductAllowedPaymentMethod } from '@diamond/shared/schema';
import { Static } from '@sinclair/typebox';

export type RegularPaymentMethod = Static<typeof ProductAllowedPaymentMethod>;

export const REGULAR_PAYMENT_METHODS: readonly RegularPaymentMethod[] = [
  'cash_on_delivery',
  'bank_transfer',
];

export type CheckoutPaymentAvailability = {
  allowedMethods: RegularPaymentMethod[];
  isUnrestricted: boolean;
  isEmptyIntersection: boolean;
};

/**
 * Intersects product-level allowed regular payment methods for Checkout.
 * Null arrays are ignored (unrestricted for that product). All-null / empty
 * product list keeps both COD and Bank Transfer. A non-null empty array
 * contributes to an empty intersection.
 */
export function getCheckoutPaymentAvailability(
  allowedPaymentMethodsByProduct: ReadonlyArray<
    ReadonlyArray<RegularPaymentMethod> | null | undefined
  >
): CheckoutPaymentAvailability {
  const configuredPaymentMethods = allowedPaymentMethodsByProduct.filter(
    (paymentMethods): paymentMethods is ReadonlyArray<RegularPaymentMethod> =>
      paymentMethods != null
  );

  if (configuredPaymentMethods.length === 0) {
    return {
      allowedMethods: [...REGULAR_PAYMENT_METHODS],
      isUnrestricted: true,
      isEmptyIntersection: false,
    };
  }

  const allowedMethods = REGULAR_PAYMENT_METHODS.filter((paymentMethod) =>
    configuredPaymentMethods.every((paymentMethods) =>
      paymentMethods.includes(paymentMethod)
    )
  );

  return {
    allowedMethods: [...allowedMethods],
    isUnrestricted: false,
    isEmptyIntersection: allowedMethods.length === 0,
  };
}

export function getFeatureGatedCheckoutPaymentAvailability(
  isFeatureEnabled: boolean,
  allowedPaymentMethodsByProduct: ReadonlyArray<
    ReadonlyArray<RegularPaymentMethod> | null | undefined
  >
): CheckoutPaymentAvailability {
  if (!isFeatureEnabled) {
    return {
      allowedMethods: [...REGULAR_PAYMENT_METHODS],
      isUnrestricted: true,
      isEmptyIntersection: false,
    };
  }

  return getCheckoutPaymentAvailability(allowedPaymentMethodsByProduct);
}
